import React from 'react';
import { Show, Button, type RaRecord } from 'react-admin';
import { Box, Typography, Paper, Chip, Alert, Grid, IconButton, Avatar } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Warning, Cancel, Close, NotificationsActive } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { STATUS_COLORS, ALERT_TYPE_COLORS } from './constants';

const ContractAlertShowContent = ({ record }: { record?: RaRecord }) => {
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();
  if (!record) return null;
  const isPastDue = record.status === 'Triggered' && new Date(record.triggerDate) < new Date();
  const isActioned = record.status === 'Actioned';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/contract-alerts')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <PageDescription entity="Contract Alert" description="An alert for employment contracts approaching expiry or requiring attention." icon={<NotificationsActive />} />
      {isPastDue && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          Alert past trigger date — immediate action required
        </Alert>
      )}
      {isActioned && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          Alert actioned — {record.actionTakenAt}
        </Alert>
      )}
      {record.status === 'Dismissed' && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Cancel />}>
          Alert dismissed
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Alert ID', value: record.alertId, bold: true },
          { label: 'Employee', value: getEmployeeName(employeeMap, record.employee?.id) },
          { label: 'Alert Type', value: record.alertType, chip: true },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Trigger Date', value: record.triggerDate, warning: isPastDue },
          { label: 'Alert Sent', value: record.alertSentAt || '—' },
          { label: 'Action Required', value: record.actionRequired },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={f.value}
                    size="small"
                    color={f.label === 'Alert Type' ? ALERT_TYPE_COLORS[record.alertType] : STATUS_COLORS[record.status]}
                  />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, color: (f as { warning?: boolean }).warning ? 'error.main' : 'inherit' }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Alert Timeline</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'grey.300', width: 24, height: 24, fontSize: 10 }}>1</Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Alert Created</Typography>
                  <Typography variant="body2">{record.alertSentAt || 'Pending'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: record.alertSentAt ? 'warning.main' : 'grey.300', width: 24, height: 24, fontSize: 10 }}>2</Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Notification Sent</Typography>
                  <Typography variant="body2">{record.alertSentAt || 'Not sent yet'}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: isActioned ? 'success.main' : 'grey.300', width: 24, height: 24, fontSize: 10 }}>3</Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary">Action Taken</Typography>
                  <Typography variant="body2">{record.actionTakenAt || 'Pending'}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button component={Link} to="/employees" variant="outlined" size="small">
                View Employee
              </Button>
              <Button component={Link} to="/onboarding-processes" variant="outlined" size="small">
                View Onboarding
              </Button>
              {record.alertType === 'ProbationExpiry' && (
                <Button component={Link} to="/disciplinary-cases" variant="outlined" size="small">
                  View Disciplinary
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {record.actionTakenAt && (
        <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
          <Typography variant="caption" color="text.secondary">Action Taken</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{record.actionTakenBy} on {record.actionTakenAt}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export const ContractAlertShow = () => (
  <Show render={(props) => <ContractAlertShowContent {...props} />} />
);
